// src/pages/PostPage.jsx
import React, { Fragment, useEffect, useRef, useState, useCallback, useContext } from "react";
import { useParams, useHistory } from "react-router-dom"; // ★ useHistory 追加
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Postview from "../components/Postview";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";
import { useTranslation } from "react-i18next";
import { gaEvent } from "../lib/ga";

const PAGE_SIZE = 20; // サーバのデフォルトに合わせてください
const IO_ROOT_MARGIN = "400px"; // 早めに発火させる
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
const normalizeHref = (url) => (url.startsWith("http") ? url : `https://${url}`);
const LinkifiedText = ({ text }) => {
  if (!text) return null;
  const parts = String(text).split(URL_REGEX);
  return parts.map((part, i) => {
    if (!part) return null;
    const isUrl = URL_REGEX.test(part);
    URL_REGEX.lastIndex = 0;
    if (!isUrl) return <span key={i}>{part}</span>;
    const href = normalizeHref(part);
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    );
  });
};

const PostPage = () => {
  const { postId } = useParams();
  const history = useHistory(); 
  const api = useAPI();
  const { newSnack } = useContext(SnackbarContext);

  const [post, setPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(true);

  // コメント
  const [comments, setComments] = useState([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
    const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  useEffect(()=>{ loadingRef.current = loadingComments; }, [loadingComments]);
  useEffect(()=>{ hasMoreRef.current = hasMore; }, [hasMore]);
  const { t } = useTranslation();

  const colRef = useRef(null); // ← コメントカードの参照
const [composerBox, setComposerBox] = useState({ left: null, width: null });

const measureComposerBox = useCallback(() => {
  const el = colRef.current;
  if (!el) return;
  const r = el.getBoundingClientRect();
  
  setComposerBox({
    left: Math.round(r.left + window.scrollX),
    width: Math.round(r.width),
  });
}, []);

useEffect(() => {
  measureComposerBox();
  const ro = new ResizeObserver(measureComposerBox);
  if (colRef.current) ro.observe(colRef.current);
  window.addEventListener("resize", measureComposerBox, { passive: true });
  window.addEventListener("scroll", measureComposerBox, { passive: true });
  window.addEventListener("orientationchange", measureComposerBox, { passive: true });
  return () => {
    ro.disconnect();
    window.removeEventListener("resize", measureComposerBox);
    window.removeEventListener("scroll", measureComposerBox);
    window.removeEventListener("orientationchange", measureComposerBox);
  };
}, [measureComposerBox]);


  // ---- 1件取得
  const loadPost = useCallback(async () => {
    try {
      setLoadingPost(true);
      const data = await api.getPostById(postId);
      setPost(data);
    } catch (e) {
      newSnack("error", e?.data?.detail || "投稿の取得に失敗しました");
    } finally {
      setLoadingPost(false);
    }
  }, [api, postId, newSnack]);

  // ---- コメントページング
const loadComments = useCallback(async (cnt, { force = false } = {}) => {
    if (!force && (loadingRef.current || !hasMoreRef.current)) return;
    try {
      setLoadingComments(true);
      const res = await api.getPostComments(postId, { count: cnt || 0 });
      const list = Array.isArray(res?.res) ? res.res : [];
      const total = typeof res?.totalCount === "number" ? res.totalCount : undefined;

      // 古い順なので、ページcnt=0が最古ページ
      setComments((prev) => (cnt === 0 ? list : [...prev, ...list]));
      if (typeof total === "number") {
        const already = (cnt === 0 ? 0 : comments.length);
        const loadedNow = already + list.length;
        setCommentsTotal(total);
        setHasMore(loadedNow < total);
      } else {
        setHasMore(list.length > 0);
      }
    } catch (e) {
      newSnack("error", e?.data?.detail || "コメントの取得に失敗しました");
      setHasMore(false);
    } finally {
      setLoadingComments(false);
    }
    // comments.length を参照するので依存に含める
    }, [api, postId, comments.length, newSnack]);

  // ---- 初期化
  useEffect(() => {
    setPost(null);
    setComments([]);
    setCommentsTotal(0);
    setPageCount(0);
    setHasMore(true);
    loadPost();
    loadComments(0, { force: true });
  }, [postId]); // eslint-disable-line

  // ---- 無限スクロール監視
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMoreRef.current) {
          const next = pageCount + 1;
          setPageCount(next);
          loadComments(next);
        }
      },
      { root: null, rootMargin: IO_ROOT_MARGIN, threshold: 0 }
    );
    if (loaderRef.current) io.observe(loaderRef.current);
    return () => io.disconnect();
  }, [pageCount, hasMore, loadingComments, loadComments]);

  // ---- コメント送信
  const submitComment = async (e) => {
    e?.preventDefault?.();
    if (!api.isAuthenticated) return newSnack("error", t("Header14"));
    const text = commentText.trim();
    if (!text) return;
    try {
      await api.addComment(postId, text);
      setCommentText("");
      // 古い順なので「最後」に付く。見た目即時反映のために楽観追加。
      const optimistic = {
        commentId: `tmp-${Date.now()}`,
        commentText: text,
        created_at: new Date().toISOString(),
        profile: {
          userId: api?.me?.userId || null,
          nickname: api?.me?.nickname || "You",
          image: api?.me?.image || "/assets/images/default_profile.png",
        },
      };
      setComments((prev) => [...prev, optimistic]);
      setCommentsTotal((v) => v + 1);
      // 必要なら末尾ページを取り直して整合性を確保
      // const tailIndex = Math.floor((commentsTotal) / PAGE_SIZE);
      // loadComments(tailIndex, { force: true });
    } catch (e2) {
      newSnack("error", e2?.data?.detail || t("Postview1"));
    }
  };

  useEffect(() => {
    if (post) {
      gaEvent("post_view", {
        post_id: postId,
        author_id: post?.profile?.userId || null,
      });
    }
  }, [post, postId]);
  
  

  return (
    <>
    <style>{`
  :root { --composer-float-h: 84px; } /* 実高さの目安（入力+送信ボタン） */
  @media (max-width: 480px){ :root { --composer-float-h: 92px; } }
  .nl-comment {
    border: 1px solid #e9ecef;          /* 枠線 */
    border-radius: 12px;                 /* 角丸 */
    background: #fff;                    /* 背景 */
    padding: 10px 12px;                  /* 内側余白 */
    transition: box-shadow .15s ease, transform .05s ease;
  }
  .nl-comment:hover {
    box-shadow: 0 2px 10px rgba(0,0,0,.04);
  }
  .nl-comment + .nl-comment {
    margin-top: 8px;                     /* コメント同士の間隔 */
  }
  .nl-comment__head {
    display: flex; align-items: center;
    gap: 8px; margin-bottom: 6px;
  }
  .nl-comment__name {
    font-weight: 700; font-size: 12px; color: #222;
  }
  .nl-comment__time {
    margin-left: auto; font-size: 11px; color: #889096;
  }
  .nl-comment__body {
    font-size: 12px; line-height: 1.5; color: #374151;
    white-space: pre-line;
  }
  .nl-comment__avatar {
    width: 28px; height: 28px; border-radius: 9999px; cursor: pointer;
  }
  /* フローティング・コンポーザー：半透明ガラス調に */
  .nl-composer {
    position: fixed;
    bottom: calc(env(safe-area-inset-bottom) + 80px);
    z-index: 1000;
    left: 50%;
    transform: translateX(-50%);
    width: min(820px, calc(100vw - 24px));
  
    /* 以前の濃いグラデから、透過グラデへ */
    background:
      linear-gradient(135deg,
        rgba(37, 99, 235, 0.28) 0%,      /* #2563eb, 28% */
        rgba(30, 64, 175, 0.28) 100%     /* #1e40af, 28% */
      );
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(15, 23, 42, .20);
    backdrop-filter: blur(10px) saturate(125%);
    -webkit-backdrop-filter: blur(10px) saturate(125%);
    color: #fff;
    padding: 10px 12px;
  }
  
  /* 低スペック / 透明効果オフ環境のフォールバック（やや濃い単色） */
  @supports not ((backdrop-filter: blur(2px))) {
    .nl-composer {
      background: rgba(37, 99, 235, 0.45);
    }
  }
  @media (prefers-reduced-transparency: reduce) {
    .nl-composer {
      background: rgba(37, 99, 235, 0.45);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
  }
  
  .nl-composer__inner {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  /* 入力欄を白いピルにして視認性UP */
  .nl-composer input.form-control {
    background: #fff !important;
    color: #0f172a !important;
    border: 0 !important;
    border-radius: 9999px !important;
    padding: 10px 14px !important;
    line-height: 1.2 !important;
    box-shadow: inset 0 0 0 1px rgba(2,6,23,.06), 0 2px 10px rgba(2,6,23,.06);
  }
  
  .nl-composer input.form-control::placeholder {
    color: #64748b;
  }
  
  /* 送信ボタン：白×ブルーで強調 */
  .nl-composer .nl-send {
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    color: #2563eb;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(255,255,255,.15), inset 0 0 0 1px rgba(2,6,23,.06);
    transition: transform .06s ease, opacity .15s ease, box-shadow .15s ease;
  }
  
  .nl-composer .nl-send:disabled {
    opacity: .6;
    cursor: not-allowed;
  }
  
  .nl-composer .nl-send:hover:not(:disabled) {
    box-shadow: 0 8px 22px rgba(255,255,255,.2), inset 0 0 0 1px rgba(2,6,23,.08);
    transform: translateY(-1px);
  }
  
  /* 件数表示は白系で */
  .nl-composer__count {
    color: rgba(255,255,255,.9);
    font-weight: 600;
    letter-spacing: .01em;
  }
  
  /* フッティングで被らないよう、ページ下に余白を確保 */
  .nl-comments-pad {
    padding-bottom: calc(var(--composer-float-h) + 64px);
  }
`}</style>

    <Fragment>
      <Header />
      <Leftnav />

      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">

            {/* 上部：ポスト本体 */}
            <div className="mb-2">
              {loadingPost ? (
                <div className="card w-100 shadow-xss rounded-xxl border-light-md p-4">{t("PostPage1")}</div>
              ) : post ? (
                // Postview のクリック遷移を無効にしたいのでラッパで止めます
                <div onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                  <Postview postData={post} />
                </div>
              ) : (
                <div className="card w-100 shadow-xss rounded-xxl border-light-md p-4">{t("PostPage2")}</div>
              )}
            </div>

                {/* コメント一覧（古い順） */}
                    <div
      ref={colRef}
      className="card w-100 shadow-xss rounded-xxl border-light-md p-3 mb-5 nl-comments-pad"
    >
      {comments.length === 0 ? (
        <div className="font-xssss text-grey-500">{t("PostPage6")}</div>
      ) : (
        comments.map((c) => {
          const created = (() => { try { return new Date(c.created_at).toLocaleString(); } catch { return c.created_at || ""; } })();
          return (
            <div key={c.commentId} className="nl-comment">
              <div className="nl-comment__head">
                <img
                  src={c.profile?.image || "/assets/images/default_profile.png"}
                  alt=""
                  className="nl-comment__avatar"
                  onClick={() => { const uid = c.profile?.userId; if (uid) history.push(`/user/${uid}`); }}
                />
                <div className="nl-comment__name pointer"
                     onClick={() => { const uid = c.profile?.userId; if (uid) history.push(`/user/${uid}`); }}
                     title={c.profile?.nickname || "User"}>
                  {c.profile?.nickname || "User"}
                </div>
                <div className="nl-comment__time">{created}</div>
              </div>
              <div className="nl-comment__body"><LinkifiedText text={c.commentText} /></div>
            </div>
          );
        })
      )}

      {/* 無限スクロール監視ターゲット */}
      <div ref={loaderRef} style={{ height: 20 }} />
      {loadingComments && <div className="font-xssss text-grey-500 mt-2">{t("PostPage1")}</div>}
    </div>

    {/* 送信コンポーザー（下部固定／背後ブラー） */}
    <div
  className="nl-composer card w-100 shadow-xss border-light-md p-0 mb-0"
  style={
    composerBox.left != null && composerBox.width != null
      ? {
          left: composerBox.left,     // ← 左端をコメント列に合わせる
          right: "calc(1cm + 30px)",         // ← 右端は常に画面端から 1cm
          maxWidth: composerBox.width, // ← コメントカード幅を上限に
          transform: "none",
        }
      : undefined
  }
>
      <div className="nl-composer__inner p-2">
        <form onSubmit={submitComment} className="d-flex align-items-center w-100">
  <input
    type="text"
    className="form-control font-xssss"
    placeholder={t("PostPage4")}
    value={commentText}
    onChange={(e) => setCommentText(e.target.value)}
    onKeyDown={(e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && commentText.trim()) {
        submitComment(e);
      }
    }}
  />
  {/* ★ アイコン送信ボタン */}
  <button
  type="submit"
  aria-label={t("PostPage5") || "送信"}
  title={t("PostPage5") || "送信"}
  disabled={!commentText.trim()}
  className="ms-2 nl-send"
>
    <i className="feather-send" />
  </button>
      </form>
      <div className="font-xssss nl-composer__count ms-2">{commentsTotal}件</div>
  </div>
            </div>
             {/* 画面最下でのダブル被り防止（Appfooter と重なる端末向け） */}
        <div style={{ height: "120px" }} />
          </div>

          <div className="middle-sidebar-right d-none d-lg-block" />
        </div>
      </div>

      <Appfooter />
    </Fragment>
    </>
  );
};

export default PostPage;
